import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronRight, ClipboardSignature, Info, Loader2 } from 'lucide-react';
import { binder, doa, studies, ApiError, type StudySiteBinderRow } from '@/api/client';
import { cn } from '@/lib/cn';
import { binderScopeSuffix, isPrimaryBinder, sortBindersPrimaryFirst } from '@/lib/binderScope';
import { PageHeader } from '@/components/layout/PageHeader';
import { studyLabel } from '@/lib/studyLabel';
import { BlindingScopeBadge } from '@/components/binder/BlindingScopeBadge';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Delegations index page.
 *
 * Shows a study/binder selector (defaulting to the most-recently-created binder
 * for this site) and the DoA status for the selected binder. Study options are
 * filtered to the caller's active site — the binder.list() call is already
 * site-scoped by the backend.
 *
 * Each row links into the per-binder grid editor at `/binder/{ref}/doa/grid`.
 */

interface BinderDoaStatus {
  binder: StudySiteBinderRow;
  loading: boolean;
  error: ApiError | null;
  has_active: boolean;
  active_version_number: number | null;
  active_status: string | null;
  has_draft: boolean;
}

const DOA_STATUS_BADGE: Record<string, string> = {
  draft:      'badge badge-neutral',
  in_review:  'badge badge-warning',
  active:     'badge badge-success',
  superseded: 'badge badge-info',
  archived:   'badge badge-neutral',
};

const DOA_STATUS_LABEL: Record<string, string> = {
  draft:      'Draft',
  in_review:  'In Review',
  active:     'Active',
  superseded: 'Superseded',
  archived:   'Archived',
};

export default function DelegationsIndexPage() {
  // The study team's binders, approved or not: a new study's first DoA has
  // to be built and signed here before anyone can open the binder itself.
  const bindersQ = useQuery({
    queryKey: ['binder', 'list', 'doa'],
    queryFn: binder.listForDoa,
    staleTime: 60_000,
  });

  const studiesQ = useQuery({
    queryKey: ['studies', 'list'],
    queryFn: studies.list,
    staleTime: 5 * 60_000,
  });
  // Study NAME first, protocol number second (client 2026-10-14, "study name
  // everywhere"; this page was missed in that sweep).
  const studyLabelMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of studiesQ.data?.rows ?? []) m.set(s.study_id, studyLabel(s));
    return m;
  }, [studiesQ.data]);

  const binders = bindersQ.data?.binders ?? [];

  // Latest study first; within a study the primary binder precedes the unblinded one.
  const sortedBinders = useMemo(() => sortBindersPrimaryFirst(binders), [binders]);

  // Selected binder ref — defaults to the latest primary binder once data arrives.
  const [selectedRef, setSelectedRef] = useState<string | null>(null);

  useEffect(() => {
    if (selectedRef) return;
    const first = sortedBinders.find(isPrimaryBinder) ?? sortedBinders[0];
    if (first?.reference_number) setSelectedRef(first.reference_number);
  }, [sortedBinders, selectedRef]);

  // Fetch DoA grid summary for all binders (used in the "all binders" table below).
  const gridQs = useQueries({
    queries: binders.map((b) => ({
      queryKey: ['doa-grid', b.reference_number ?? b.binder_id],
      queryFn: () => doa.getGrid(b.reference_number ?? b.binder_id),
      enabled: !!(b.reference_number ?? b.binder_id),
      staleTime: 30_000,
      retry: false,
    })),
  });

  const rows: BinderDoaStatus[] = useMemo(() => {
    return binders.map((b, idx) => {
      const q = gridQs[idx]!;
      const err = q.error instanceof ApiError ? q.error : null;
      const noDoaYet = err?.status === 404;
      const version = q.data?.version ?? null;
      return {
        binder: b,
        loading: q.isLoading,
        error: noDoaYet ? null : err,
        has_active: !!version && version.status === 'active',
        active_version_number: version?.version_number ?? null,
        active_status: version?.status ?? null,
        has_draft: !!version && version.status === 'draft',
      };
    });
  }, [binders, gridQs]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.binder.reference_number === selectedRef) ?? null,
    [rows, selectedRef],
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title="Delegations of Authority"
        subtitle="One DoA log per binder. Select a study to view or edit its delegation grid. Study options are filtered to your active site."
      />

      {/* ── Study selector ──────────────────────────────────────── */}
      <div className="flex items-end gap-4 flex-wrap">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
            Study
          </label>
          {bindersQ.isLoading ? (
            <div className="flex items-center gap-2 h-10 px-3 text-ink-muted text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading studies…
            </div>
          ) : (
            <div className="relative">
              <select
                value={selectedRef ?? ''}
                onChange={(e) => setSelectedRef(e.target.value)}
                className={cn(
                  'appearance-none rounded border border-border bg-white py-2 pl-3 pr-9',
                  'text-sm font-medium text-ink-primary outline-none min-w-[280px]',
                  'focus:border-navy focus:ring-1 focus:ring-navy/20',
                )}
              >
                {sortedBinders.length === 0 && (
                  <option value="">No binders available</option>
                )}
                {sortedBinders.map((b) => {
                  const proto = studyLabelMap.get(b.study_id ?? '');
                  const label =
                    (proto
                      ? `${proto} (${b.reference_number || b.binder_id})`
                      : (b.reference_number ?? b.binder_id) + (b.binder_name ? ` — ${b.binder_name}` : '')) +
                    binderScopeSuffix(b);
                  return (
                    <option key={b.binder_id} value={b.reference_number ?? b.binder_id}>
                      {label}
                    </option>
                  );
                })}
              </select>
              <ChevronDown
                size={14}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted"
              />
            </div>
          )}
        </div>

        {selectedRow && (
          <Link
            to={`/binder/${encodeURIComponent(selectedRef ?? '')}/doa/grid`}
            className="btn-primary text-sm"
          >
            <ClipboardSignature className="h-4 w-4" />
            Open delegation grid
          </Link>
        )}
      </div>

      {/* ── Selected binder DoA status card ─────────────────────── */}
      {selectedRow && (
        <div className="rounded border border-surface-border bg-white overflow-hidden">
          <div className="border-b border-surface-border bg-surface-muted/40 px-4 py-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ink-muted">
              <span>
                Delegation of Authority —{' '}
                {(() => {
                  const proto = studyLabelMap.get(selectedRow.binder.study_id ?? '');
                  return proto
                    ? `${proto} (${selectedRow.binder.reference_number})`
                    : selectedRow.binder.reference_number;
                })()}
              </span>
              <BlindingScopeBadge scope={selectedRow.binder.blinding_scope} />
            </div>
            {selectedRow.binder.binder_name && (
              <div className="mt-0.5 text-[13px] text-ink-muted">
                {selectedRow.binder.binder_name}
              </div>
            )}
          </div>
          <div className="p-4 flex items-center gap-6 flex-wrap">
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] uppercase tracking-wider text-ink-muted">Status</span>
              {selectedRow.loading ? (
                <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                </span>
              ) : selectedRow.error ? (
                <span className="text-xs text-danger-700">Error loading DoA</span>
              ) : selectedRow.active_status ? (
                <span className={`${DOA_STATUS_BADGE[selectedRow.active_status] ?? 'badge badge-neutral'} text-[11px]`}>
                  {DOA_STATUS_LABEL[selectedRow.active_status] ?? selectedRow.active_status}
                </span>
              ) : (
                <span className="text-xs text-ink-muted italic">No DoA yet</span>
              )}
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] uppercase tracking-wider text-ink-muted">Version</span>
              <span className="font-mono text-sm text-ink-primary">
                {selectedRow.active_version_number ? `v${selectedRow.active_version_number}` : '—'}
              </span>
            </div>
            <div className="ml-auto flex gap-2">
              <Link
                to={`/binder/${encodeURIComponent(selectedRef ?? '')}/doa`}
                className="btn-ghost text-xs"
              >
                Version history
              </Link>
              <Link
                to={`/binder/${encodeURIComponent(selectedRef ?? '')}/doa/grid`}
                className="btn-primary text-xs"
              >
                Open grid
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {bindersQ.isError && (
        <div className={`mt-6 flex items-start gap-2 rounded border p-4 text-sm ${errorToneClass(bindersQ.error)}`}>
          {isPermissionError(bindersQ.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>
            {errorText(bindersQ.error, 'Failed to load binders.')}
          </span>
        </div>
      )}

      {bindersQ.data && binders.length === 0 && (
        <div className="rounded border border-surface-border bg-surface-muted/30 p-10 text-center text-sm text-ink-muted">
          No binders at this site yet. Activate a study to provision its eISF binder.
        </div>
      )}

      {/* ── All binders overview table ───────────────────────────── */}
      {rows.length > 1 && (
        <div>
          <div className="mb-2 text-[12px] font-semibold uppercase tracking-wider text-ink-muted">
            All studies at this site
          </div>
          <div className="rounded border border-surface-border bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-surface-border bg-surface-muted/40 text-left text-xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="px-4 py-2.5">Study / Binder</th>
                  <th className="px-4 py-2.5">DoA status</th>
                  <th className="px-4 py-2.5">Version</th>
                  <th className="px-4 py-2.5 text-right">Open grid</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <DoaRow
                    key={r.binder.binder_id}
                    r={r}
                    protocolNumber={studyLabelMap.get(r.binder.study_id ?? '')}
                    isSelected={r.binder.reference_number === selectedRef}
                    onSelect={() => setSelectedRef(r.binder.reference_number)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function DoaRow({
  r,
  protocolNumber,
  isSelected,
  onSelect,
}: {
  r: BinderDoaStatus;
  protocolNumber?: string;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const ref = r.binder.reference_number ?? r.binder.binder_id;
  return (
    <tr
      className={cn(
        'border-b border-surface-border/40 last:border-0 cursor-pointer',
        isSelected ? 'bg-navy-50' : 'hover:bg-surface-muted/30',
      )}
      onClick={onSelect}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 font-medium text-ink-primary">
          <span>
            {protocolNumber
              ? `${protocolNumber} (${r.binder.reference_number ?? r.binder.binder_id})`
              : (r.binder.reference_number ?? <span className="text-danger-700">No refnum</span>)}
          </span>
          <BlindingScopeBadge scope={r.binder.blinding_scope} />
        </div>
        {r.binder.binder_name && (
          <div className="text-xs text-ink-muted truncate max-w-md mt-0.5">{r.binder.binder_name}</div>
        )}
      </td>
      <td className="px-4 py-3">
        {r.loading ? (
          <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
          </span>
        ) : r.error ? (
          <span className="inline-flex items-center gap-1 text-xs text-danger-700" title={r.error.message}>
            <AlertTriangle className="h-3 w-3" /> Error
          </span>
        ) : r.active_status ? (
          <span className={`${DOA_STATUS_BADGE[r.active_status] ?? 'badge badge-neutral'} text-[11px]`}>
            {DOA_STATUS_LABEL[r.active_status] ?? r.active_status}
          </span>
        ) : (
          <span className="text-xs text-ink-muted italic">No DoA yet</span>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-ink-muted">
        {r.active_version_number ? `v${r.active_version_number}` : '—'}
      </td>
      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
        <Link
          to={`/binder/${encodeURIComponent(ref)}/doa/grid`}
          className="inline-flex items-center gap-1 rounded border border-surface-border bg-white px-2.5 py-1 text-xs font-medium text-ink-primary hover:bg-surface-muted/50"
        >
          Open grid
          <ChevronRight className="h-3 w-3" />
        </Link>
      </td>
    </tr>
  );
}
